import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styles } from './ProductGrid.css';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { ContentfulProductGridMobileLayout } from '@jbhi-fi/apis/src/contentful/types';
import { HeadingDivider } from '../HeadingDivider/HeadingDivider';
import { ProductFallback, ProductFallbackProps } from '../ProductFallback/ProductFallback';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { useIsOnScreen } from '../hooks/useIsOnScreen/useIsOnScreen';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '../Algolia/transformers/transformAlgoliaHitForTracking';
import { UseProductsProps, useProducts, filterUniqueProducts } from '../hooks/useProducts/useProducts';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { NoResultsPlaceholder } from '../NoResultsPlaceholder/NoResultsPlaceholder';
import { ProductCardProps } from '../ProductCard/types';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { useGridColumns } from '../hooks/useGridColumns/useGridColumns';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';

const DEFAULT_DESKTOP_ROWS = 2;
const DEFAULT_PRODUCTS_PER_ROW = 6;
const MOBILE_DECK_PRODUCTS_PER_PAGE = 12;

export type ProductGridProps = {
  heading?: string;
  className?: string;
  renderProduct: (data: { hit: AlgoliaProductHit; position: number }) => React.ReactNode;
  productCardSkeletonProps?: ProductFallbackProps['productCardSkeletonProps'];
  shouldTrackImpressions?: boolean;
  trackingListName?: string;
  productCardVariant?: ProductCardProps['variant'];
  desktopRows?: number;
  mobileLayout?: ContentfulProductGridMobileLayout;
} & UseProductsProps;

export const ProductGrid = ({
  heading,
  className,
  renderProduct,
  productCardSkeletonProps = {},
  shouldTrackImpressions = false,
  trackingListName,
  productCardVariant = 'tablet',
  desktopRows = DEFAULT_DESKTOP_ROWS,
  mobileLayout = ContentfulProductGridMobileLayout.Stack,
  ...useProductsProps
}: ProductGridProps) => {
  const [page, setPage] = useState(0);
  const [loadedProducts, setLoadedProducts] = useState<Array<AlgoliaProductHit>>([]);
  const [productsRef, productsPerRow] = useGridColumns();
  const isMobile = useBreakpoint('sm', 'down');
  const isMobileDeck = isMobile && mobileLayout === ContentfulProductGridMobileLayout.Deck;
  const normalisedDesktopRows = Math.max(1, desktopRows);
  const productsPerPage = isMobileDeck
    ? MOBILE_DECK_PRODUCTS_PER_PAGE
    : (productsPerRow || DEFAULT_PRODUCTS_PER_ROW) * normalisedDesktopRows;
  const requestSignature = `${isMobileDeck}-${productsPerPage}`;
  const previousRequestSignatureRef = useRef(requestSignature);
  const isDeckPageRequestInFlightRef = useRef(false);

  const {
    products = [],
    numberOfProducts,
    isLoading,
  } = useProducts({
    isEnabled: useProductsProps.isEnabled ?? true,
    algoliaSearchOptions: {
      hitsPerPage: productsPerPage,
      page,
    },
    ...useProductsProps,
  });
  const fetchedProductsKey = products?.reduce((acc, product) => `${acc}-${product.sku}`, '') ?? '';
  const fetchedProducts = useMemo(() => products, [fetchedProductsKey]);
  const displayedProducts = loadedProducts.length ? loadedProducts : fetchedProducts;
  const productsForDisplay =
    isLoading && !displayedProducts.length ? Array.from(Array(productsPerPage)).fill(null) : displayedProducts;
  const hasNoProducts = !isLoading && !displayedProducts.length;
  const hasMoreProducts =
    numberOfProducts > 0 ? displayedProducts.length < numberOfProducts : fetchedProducts.length === productsPerPage;
  const containerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useIsOnScreen(containerRef, { persistOnceSeen: true });

  useEffect(() => {
    if (previousRequestSignatureRef.current === requestSignature) return;
    previousRequestSignatureRef.current = requestSignature;
    setPage(0);
    setLoadedProducts([]);
    isDeckPageRequestInFlightRef.current = false;
  }, [requestSignature]);

  useEffect(() => {
    if (!isLoading) {
      isDeckPageRequestInFlightRef.current = false;
    }
  }, [isLoading]);

  useEffect(() => {
    if (isLoading) return;

    if (page === 0) {
      setLoadedProducts(fetchedProducts);
      return;
    }

    if (fetchedProducts?.length) {
      setLoadedProducts(prev => filterUniqueProducts(...prev, ...fetchedProducts));
    }
  }, [isLoading, page, fetchedProducts]);

  useEffect(() => {
    if (!isOnScreen || isLoading || !fetchedProducts.length) return;

    trackViewItemList({
      currency: getStoreCurrency(),
      listName: trackingListName,
      viewedItems: transformAlgoliaHitForTracking(fetchedProducts),
    });

    if (shouldTrackImpressions) {
      window?.gtm?.track?.('productImpression', {
        products: fetchedProducts.map((product, i) => ({
          ...product.google_tag_manager,
          list: trackingListName,
          position: page * productsPerPage + i + 1,
        })),
      });
    }
  }, [isOnScreen, isLoading, page, fetchedProducts, productsPerPage, shouldTrackImpressions, trackingListName]);

  const handleDeckSwipe = (activeIndex?: number) => {
    if (!isMobileDeck || activeIndex === undefined || isLoading || isDeckPageRequestInFlightRef.current) return;

    const isNearEnd = activeIndex >= displayedProducts.length - 2;
    if (!isNearEnd || !hasMoreProducts) return;

    isDeckPageRequestInFlightRef.current = true;
    setPage(prev => prev + 1);
  };

  if (hasNoProducts) {
    return (
      <NoResultsPlaceholder
        className={className}
        icon="LecternIllustrated"
        title="Looks like this shelf is empty"
        contents="We’re restocking this section... in the meantime check out our other deals."
      />
    );
  }

  return (
    <div className={classNames(styles.container, className)} ref={containerRef}>
      {heading ? (
        <div className={styles.headingContainer}>{heading ? <HeadingDivider heading={heading} /> : null}</div>
      ) : null}

      {isMobileDeck ? (
        <Carousel
          centeredSlides
          slidesPerView="auto"
          freeMode={false}
          progressBarBulletShape="dash"
          onSlideChange={swiper => handleDeckSwipe(swiper?.activeIndex)}
          variant="cards"
          grabCursor={true}
          fullBleed
        >
          {productsForDisplay.map((product, i) => (
            <ProductFallback
              isLoading={isLoading && !displayedProducts.length}
              product={product}
              key={product?.objectID ?? product?.sku ?? `${i}`}
              productCardSkeletonProps={{ variant: 'deck', ...productCardSkeletonProps }}
            >
              {product ? renderProduct({ hit: product, position: i + 1 }) : null}
            </ProductFallback>
          ))}
        </Carousel>
      ) : (
        <div className={styles.products({ productCardVariant })} ref={productsRef}>
          {productsForDisplay.map((product, i) => (
            <ProductFallback
              isLoading={isLoading && !displayedProducts.length}
              product={product}
              key={product?.objectID ?? product?.sku ?? `${i}`}
              productCardSkeletonProps={{ variant: 'tablet', ...productCardSkeletonProps }}
            >
              {product ? renderProduct({ hit: product, position: i + 1 }) : null}
            </ProductFallback>
          ))}
        </div>
      )}
      {!isLoading && !isMobileDeck ? (
        <div className={styles.bottomSection}>
          <ProgressBar
            currentItemsCount={displayedProducts.length}
            totalItemsCount={numberOfProducts}
            shouldShowText
            className={styles.progressBar}
          />
          {hasMoreProducts ? (
            <Button
              classes={styles.loadMoreButton}
              type="primary"
              label="Load more"
              onClick={() => setPage(prev => prev + 1)}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
